import { FC, ReactNode, useEffect, useState } from "react"
import { useNavigate, useParams, useLocation } from "react-router-dom"
import { observer } from "mobx-react-lite"
import { Button, FormattedMessage, PageTitle, Table, useIntl, useToast, ToastLevel, Grid, GridColumn } from "@metsooutotec/modes-react-components"
import customerStore from "features/organizations/customers/store/customer-store"
import { NavigationDeleteEntityOptions, NavigationModalOptions } from "components/utils/modal-helper"
import { GenerateBreadcrumbs } from "../../utils/customers-bread-crumbs"
import { NotFound } from "components/utils/not-found"
import {
    itemViewCellStyles,
    itemViewDetailsContainerStyles,
    itemViewDetailsTableStyles,
    itemViewListContainerStyles,
    itemViewSeparatorStyles,
    itemViewSubtitleStyles,
} from "components/common-style/common-style"
import CustomerModal from "../../components/customer-modal"
import EntityDeleteModal from "../../components/entity-delete-modal"
import { ModalTypes } from "../../utils/customer-modal-helper"
import UsersCard from "features/organizations/common/cards/users-cards/users-card"
import SitesCard from "../../components/sites-card/sites-card"

const CustomerView: FC = observer(() => {
    const { formatMessage } = useIntl()
    const { setMessage } = useToast()
    const navigate = useNavigate()
    const { customerId } = useParams()
    const location = useLocation()
    const { customerMap } = customerStore
    const customer = customerMap[customerId || ""]
    const params: NavigationModalOptions = location.state
    const deleteParams: NavigationDeleteEntityOptions = location.state
    const { deleted: DeletedSite, entityName: SiteName } = deleteParams || {}
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false)

    //if siteToEdit is undefinded the modal will open with empty fields to create a new site.

    const breadcrumbsPath = GenerateBreadcrumbs(customer, undefined, undefined, undefined)

    const showNotifier = (text: string) => {
        setMessage(text, ToastLevel.SUCCESS, 6000)
    }
    const customerModal = {
        open: () => {
            setIsModalOpen(true)
        },
        close: (passed: boolean, customerId?: string, navigationCustomerOptions?: NavigationModalOptions) => {
            setIsModalOpen(false)
            if (passed) {
                navigate(`/customers/${customerId}`, {
                    state: navigationCustomerOptions,
                })
            }
        },
    }
    const deleteCustomerModal = {
        open: () => {
            setIsDeleteModalOpen(true)
        },
        close: (params: NavigationDeleteEntityOptions) => {
            setIsDeleteModalOpen(false)
            if (params.deleted) {
                navigate(`/customers`, { state: params })
            }
        },
    }

    useEffect(() => {
        if (params?.type === "created") {
            showNotifier("New Customer created successfully")
        }
        if (params?.type === "edited") {
            showNotifier("All changes made successfully")
        }
        window.history.replaceState({}, document.title)
    }, [params])

    useEffect(() => {
        if (DeletedSite) {
            showNotifier(`You have deleted site '${SiteName}'.`)
        }
        window.history.replaceState({}, document.title)
    }, [DeletedSite, SiteName, deleteParams])

    const rightPageButtons: ReactNode = (
        <>
            <Button icon='icon-edit-16' labelPosition='right' onClick={customerModal.open} primary>
                {formatMessage("customer.edit")}
            </Button>
            <Button icon='icon-trash-16' labelPosition='left' onClick={deleteCustomerModal.open} secondary>
                {formatMessage("customer.delete")}
            </Button>
        </>
    )

    return !customer ? (
        <NotFound id={customerId} />
    ) : (
        <>
            <PageTitle breadcrumbsPath={breadcrumbsPath} onPathChange={navigate} title={customer.name} extraItems={rightPageButtons} />
            <div style={itemViewDetailsContainerStyles}>
                <h3 style={itemViewSubtitleStyles}>
                    <FormattedMessage id='customer.details' />
                </h3>
                <Table style={itemViewDetailsTableStyles} tight>
                    <Table.Body>
                        <Table.Row>
                            <Table.HeaderCell style={itemViewCellStyles}>
                                <FormattedMessage id='headings.contact' />
                            </Table.HeaderCell>
                            <Table.Cell style={itemViewCellStyles}>{customer.contact}</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.HeaderCell style={itemViewCellStyles}>
                                <FormattedMessage id='headings.metsoContact' />
                            </Table.HeaderCell>
                            <Table.Cell style={itemViewCellStyles}>{customer.metsoContact}</Table.Cell>
                        </Table.Row>
                    </Table.Body>
                </Table>
            </div>
            <div style={itemViewSeparatorStyles}></div>
            <div style={itemViewListContainerStyles}>
                <Grid columns={2}>
                    <GridColumn>
                        <SitesCard />
                    </GridColumn>
                    <GridColumn>
                        <UsersCard moduleType='customer' id={customerId as string} />
                    </GridColumn>
                </Grid>
            </div>
            {isModalOpen && <CustomerModal isOpen={isModalOpen} onClose={customerModal.close} customer={customer} />}
            {isDeleteModalOpen && (
                <EntityDeleteModal
                    isOpen={isDeleteModalOpen}
                    onClose={deleteCustomerModal.close}
                    entity={customer}
                    modalType={ModalTypes.CustomerModal}
                    customerId={customer.id}
                />
            )}
        </>
    )
})

export default CustomerView
